import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  HostListener,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
} from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BudgetValidation, ValidateSpace } from '@vinnova/validation';
import { format } from 'date-fns';
import { deepEqual } from 'fast-equals';
import {
  debounceTime,
  distinctUntilChanged,
  merge,
  Observable,
  skip,
  Subscription,
  take,
} from 'rxjs';
import { InputType } from '../../../models/input-type.model';
import { ButtonComponent } from '../../button/button.component';
import { ModalRef } from '../../modal/modal-ref';
import { ModalService } from '../../modal/service/modal.service';
import {
  ColumnRanges,
  IRowValidation,
  ITableChange,
  ITableColumn,
  ITableRow,
  TableRowValueList,
} from './interactive-table.interface';

@UntilDestroy()
@Component({
  selector: 'lib-interactive-table',
  templateUrl: './interactive-table.component.html',
  styleUrls: ['./interactive-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InteractiveTableComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  id = input<string>();
  title = input<string>();
  rows = input<ITableRow[]>();
  column: ITableColumn;
  columns = input<ITableColumn[]>();
  tierTwoColumnHeaders = input<ITableColumn[]>([]);
  size = input<'small' | 'medium' | 'large'>('large');
  sumRow = input<boolean>();
  sumCol = input<boolean>();
  maxColsDesktop = input<number>();
  maxColsMobile = input<number>();
  expanded = input<boolean>();
  required = input<boolean>();
  expandedModalInput = input<ModalRef<InteractiveTableComponent>>();
  inputType = input<InputType>();
  suffix = input<string>();
  hideExpand = input<boolean>();
  tableContentToRight = input<boolean>();
  readonly = input<boolean>();
  readOnly$ = input<Observable<boolean>>();
  readonlyData = input<TableRowValueList>();
  tableHeading = input<string>();
  mobileTableHeading = input<string>();
  totalHeading = input<string>();
  expansionTitle = input<string>();
  expansionSubTitle = input<string>();
  tableValidation = input<string[]>();
  tableRowValidation = input<IRowValidation[]>();
  disableResize = input<boolean>();
  defaultValue = input<number>();
  checkValidation$ = input<Observable<boolean>>();
  updateValidation$ = input<Observable<boolean>>();
  hideValidation = input<boolean>();
  borderLeft = input<boolean>();
  allowMinus = input<boolean>();
  browseLarge = input<ColumnRanges>();
  browseMedium = input<ColumnRanges>();
  browseSmall = input<ColumnRanges>();
  markAllAsTouched = input<boolean>();
  touched = input<boolean>();
  changes = input<ITableChange[][][]>();

  showToggle = input<boolean>();
  toggleActiveText = input<string>();
  toggleInactiveText = input<string>();
  toggleOutput = output<boolean>();
  toggleState = false;

  saveExpanded = output<boolean>();
  tableTouched = output<void>();

  subscribes: Subscription[];
  expandedModalRef: ModalRef<InteractiveTableComponent>;
  readOnlyState: boolean;

  expandBtnRef = viewChild<ButtonComponent>('expandBtn');
  animatedBtnRef = viewChild<ElementRef>('animatedBtn');
  tableRef = viewChild<ElementRef>('table');

  touchedSignal = signal(false);
  showRowSum = signal(false);
  startCol = 0;
  showCol: number[];
  browseIndex = 0;
  rowSums: number[];
  colSums: number[];
  totalSum: number;
  values: {
    rowId: string;
    columnId: string;
    control: FormControl<string | number>;
    invalid: boolean;
  }[][];
  readonlyValues: string[][];
  maxCols: number;

  resizeColumnWidth: number;
  resizeX: number;
  resizeColumn: HTMLElement;
  notExpandedAndShowExpand: boolean;
  tableInvalid: boolean;
  errors: string[];
  warnings: string[];
  rowInvalid: string[];
  rowWarning: string[];
  valueBeforeExpansion: string;
  smallNumbers: boolean;
  saveOnExpansionHide: boolean;
  disabled: boolean;
  isMobile: boolean;
  isAndroid: boolean;
  controlHasBeenDirty: boolean;
  hasDoubleTitleRow: boolean;
  isPrinting = false;

  prevColumns;
  prevRows;
  prevReadonlyData;
  prevTableRowValidation;

  get activeLang() {
    return this.translocoService.getActiveLang();
  }

  public readonly BudgetValidation: typeof BudgetValidation = BudgetValidation;
  constructor(
    private modalService: ModalService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private translocoService: TranslocoService,
    @Optional() public ngControl: NgControl,
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }

    effect(() => {
      if (
        (this.prevColumns && !deepEqual(this.prevColumns, this.columns())) ||
        (this.prevRows && !deepEqual(this.prevRows, this.rows()))
      ) {
        this.setShowCols();
        this.setValues();
      }
      this.prevColumns = this.columns();
      this.prevRows = this.rows();
    });

    effect(() => {
      if (this.readonlyData() && this.prevReadonlyData) {
        untracked(() => {
          if (this.expanded() || !this.expandedModalRef) {
            this.setReadonlyValues();
            this.cdr.markForCheck();
          }
        });
      }
      this.prevReadonlyData = this.readonlyData();
    });

    effect(() => {
      if (this.tableRowValidation() && this.prevTableRowValidation) {
        this.setValidation();
      }
      this.prevTableRowValidation = this.tableRowValidation();
    });

    effect(() => {
      if (this.markAllAsTouched() && this.values) {
        for (const value of this.values.flat()) {
          value.control.markAsTouched();
        }
      }
    });

    effect(() => {
      this.touchedSignal.set(this.touched());
    });
  }

  ngOnInit(): void {
    this.isMobile =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        navigator.userAgent,
      );

    this.isAndroid = /android/i.test(navigator.userAgent);

    // Setup print event listeners to show all columns when printing
    globalThis.addEventListener('beforeprint', this.handleBeforePrint);
    globalThis.addEventListener('afterprint', this.handleAfterPrint);

    if (this.readOnly$()) {
      this.readOnly$()
        .pipe(untilDestroyed(this))
        .subscribe((val) => {
          this.readOnlyState = val;
          this.notExpandedAndShowExpand = !(
            this.hideExpand() || this.expanded()
          );
          this.setValues(this.ngControl?.value);
          this.setShowCols();
        });
    } else {
      this.setValues(this.ngControl?.value);
      this.setShowCols();
    }
    this.rowInvalid = [];
    this.rowWarning = [];

    if (this.tableValidation() || this.tableRowValidation()) {
      this.setValidation();
    }

    if (this.expanded()) {
      this.modalService
        .onHide(this.expandedModalInput())
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.saveExpanded.emit(this.saveOnExpansionHide);
        });
    }

    if (this.checkValidation$()) {
      this.checkValidation$()
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.ngControl.control.markAsTouched();
          this.setValidation();
          if (this.values) {
            for (const value of this.values.flat()) {
              value.control.markAsTouched();
              value.control.markAsDirty();
            }
          }

          if (this.required()) {
            const isEmpty = this.values?.flat().every((v) => v.invalid);
            if (isEmpty) {
              this.tableInvalid = true;
              this.errors.push('required');
            }
          }
        });
    }

    if (this.updateValidation$()) {
      this.updateValidation$()
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.setValidation();
        });
    }

    this.hasDoubleTitleRow = this.columns().some((c) => c.titleRow2);
  }

  ngOnDestroy(): void {
    globalThis.removeEventListener('beforeprint', this.handleBeforePrint);
    globalThis.removeEventListener('afterprint', this.handleAfterPrint);
  }

  private handleBeforePrint = () => {
    this.isPrinting = true;
    this.setShowCols();
    this.cdr.detectChanges();
  };

  private handleAfterPrint = () => {
    this.isPrinting = false;
    this.setShowCols();
    this.cdr.detectChanges();
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_value: unknown) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  writeValue(value: string): void {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.setValues(value);
      });
    });
  }

  registerOnChange(fn: (value) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (!this.values) {
      return;
    }
    for (const value of this.values.flat()) {
      if (isDisabled) {
        value.control.disable();
      } else {
        value.control.enable();
      }
    }
  }

  openExpandedModal(content: TemplateRef<InteractiveTableComponent>) {
    this.valueBeforeExpansion = this.ngControl?.control?.value;
    this.expandedModalRef = this.modalService.show(content, {
      backdrop: 'static',
      class: 'modal-xxl modal-fullscreen-xxl-down table-modal',
    });
    this.modalService
      .onHide(this.expandedModalRef)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.expandedModalRef = undefined;
        this.touchedSignal.set(true);
      });
  }

  onCloseExpandedModal(save: boolean) {
    this.saveOnExpansionHide = save;
    if (this.expandedModalInput()) {
      this.expandedModalInput().hide();
    } else {
      this.expandedModalRef.hide();
    }
  }

  saveExpansionValues(save: boolean) {
    this.saveExpanded.emit(save);
    if (!save) {
      this.ngControl?.control.patchValue(this.valueBeforeExpansion);
      this.valueBeforeExpansion = undefined;
      return;
    }
    this.valueBeforeExpansion = undefined;

    this.setValues(this.ngControl?.control.value);
    this.ngControl?.control.markAsTouched();

    this.tableInvalid =
      this.ngControl?.invalid &&
      this.tableValidation() &&
      this.tableValidation().some((v) => this.ngControl?.errors[v]);
    this.errors = [];
    this.warnings = [];
    this.cdr.markForCheck();

    if (this.tableInvalid) {
      this.errors.push(
        ...this.tableValidation().filter((v) => this.ngControl?.errors[v]),
      );
    }

    this.setWarnings();
    this.setRowValidation();
  }

  browsePrev() {
    if (window.innerWidth >= 992 && this.browseLarge()) {
      this.browseIndex--;
      this.setBrowse(this.browseLarge());
    } else if (window.innerWidth >= 576 && this.browseMedium()) {
      this.browseIndex--;
      this.setBrowse(this.browseMedium());
    } else if (window.innerWidth < 576 && this.browseSmall()) {
      this.browseIndex--;
      this.setBrowse(this.browseSmall());
    } else {
      this.startCol = this.startCol - 1;
      this.showCol = this.showCol.map((i) => i - 1);
    }
  }

  browseNext() {
    if (window.innerWidth >= 992 && this.browseLarge()) {
      this.browseIndex++;
      this.setBrowse(this.browseLarge());
    } else if (window.innerWidth >= 576 && this.browseMedium()) {
      this.browseIndex++;
      this.setBrowse(this.browseMedium());
    } else if (window.innerWidth < 576 && this.browseSmall()) {
      this.browseIndex++;
      this.setBrowse(this.browseSmall());
    } else {
      this.startCol = this.startCol + 1;
      this.showCol = this.showCol.map((i) => i + 1);
    }
  }

  inputBlur($event) {
    $event.target.parentElement.parentElement.style.zIndex = 'unset';
    this.tableTouched.emit();
  }

  checkInvalid(
    control: FormControl<string>,
    rowIndex: number,
    columnIndex: number,
  ) {
    if (!this.errors) {
      this.errors = [];
    }

    if (control.dirty) {
      this.controlHasBeenDirty = true;
    }

    const showValidation =
      control.invalid && this.controlHasBeenDirty && control.touched;
    if (showValidation) {
      for (const value of this.values.flat()) {
        value.control.markAsTouched();
      }
    }
    this.values[rowIndex][columnIndex].invalid = control.invalid;
    this.tableInvalid =
      (this.values.flat().every((v) => v.invalid) && showValidation) ||
      this.errors.includes('SkissBudgetKostnaderRequired');

    if (this.tableInvalid) {
      this.errors.push('required');
    } else {
      this.errors = this.errors.filter((e) => e !== 'required');
    }
    this.cdr.markForCheck();
  }

  @HostListener('keydown.ArrowDown', ['$event'])
  onArrowDown($event) {
    if (!this.focusInTable()) {
      return;
    }

    if (
      this.tableRef().nativeElement === document.activeElement &&
      document.activeElement.classList.contains('focus-visible')
    ) {
      const input = this.tableRef().nativeElement.querySelector('input');
      this.setFocus(input);
    } else {
      const rowElement = document.activeElement.parentElement.parentElement;
      const columnIndex =
        [...rowElement.parentElement.children].indexOf(rowElement) - 1;

      const input =
        rowElement.parentElement.nextElementSibling?.querySelectorAll('input')[
          columnIndex
        ] ?? (document.activeElement as HTMLElement);
      this.setFocus(input);
    }
    $event.stopImmediatePropagation();
  }

  @HostListener('keydown.ArrowUp', ['$event'])
  onArrowUp($event) {
    if (!this.focusInTable()) {
      return;
    }

    if (
      this.tableRef().nativeElement === document.activeElement &&
      document.activeElement.classList.contains('focus-visible')
    ) {
      const input = this.tableRef().nativeElement.querySelector('input');
      this.setFocus(input);
    } else {
      const rowElement = document.activeElement.parentElement.parentElement;
      const columnIndex =
        [...rowElement.parentElement.children].indexOf(rowElement) - 1;

      const input =
        rowElement.parentElement.previousElementSibling?.querySelectorAll(
          'input',
        )[columnIndex] ?? (document.activeElement as HTMLElement);
      this.setFocus(input);
    }
    $event.stopImmediatePropagation();
  }

  @HostListener('keydown.ArrowRight', ['$event'])
  onArrowRight($event) {
    if (!this.focusInTable()) {
      return;
    }

    if (
      this.tableRef().nativeElement === document.activeElement &&
      document.activeElement.classList.contains('focus-visible')
    ) {
      const input = this.tableRef().nativeElement.querySelector('input');
      this.setFocus(input);
    } else {
      const input =
        document.activeElement.parentElement.parentElement.nextElementSibling?.querySelector(
          'input',
        ) ?? (document.activeElement as HTMLElement);
      this.setFocus(input);
    }
    $event.stopImmediatePropagation();
  }

  @HostListener('keydown.ArrowLeft', ['$event'])
  onArrowLeft($event) {
    if (!this.focusInTable()) {
      return;
    }

    if (
      this.tableRef().nativeElement === document.activeElement &&
      document.activeElement.classList.contains('focus-visible')
    ) {
      const input = this.tableRef().nativeElement.querySelector('input');
      this.setFocus(input);
    } else {
      const input =
        document.activeElement.parentElement.parentElement.previousElementSibling?.querySelector(
          'input',
        ) ?? (document.activeElement as HTMLElement);
      this.setFocus(input);
    }
    $event.stopImmediatePropagation();
  }

  @HostListener('keydown.enter', ['$event'])
  onEnter($event) {
    if (
      this.tableRef().nativeElement === document.activeElement &&
      document.activeElement.classList.contains('focus-visible')
    ) {
      const input = this.tableRef().nativeElement.querySelector('input');
      this.setFocus(input);
    } else if (!document.activeElement.classList.contains('focus-visible')) {
      this.goToNextCell();
      $event.stopImmediatePropagation();
    }
  }

  @HostListener('keydown.esc', ['$event'])
  onEsc($event) {
    if (
      (this.tableRef().nativeElement as HTMLElement).contains(
        document.activeElement,
      ) &&
      !document.activeElement.classList.contains('focus-visible')
    ) {
      document.activeElement.classList.add('focus-visible');
      $event.stopImmediatePropagation();
    }
  }

  @HostListener('keydown.tab', ['$event'])
  onTab($event) {
    if (
      this.tableRef().nativeElement.contains(document.activeElement) &&
      !document.activeElement.classList.contains('focus-visible')
    ) {
      this.goToNextCell();
      $event.preventDefault();
    }
  }

  @HostListener('keydown.shift.tab', ['$event'])
  onBackTab($event) {
    if (
      this.tableRef().nativeElement.contains(document.activeElement) &&
      !document.activeElement.classList.contains('focus-visible')
    ) {
      let prevInput =
        document.activeElement.parentElement.parentElement.previousElementSibling?.querySelector(
          'input',
        );
      if (!prevInput) {
        const rowElement = document.activeElement.parentElement.parentElement;
        prevInput =
          rowElement.parentElement.previousElementSibling?.querySelector(
            'input',
          ) ?? (document.activeElement as HTMLInputElement);
      }
      this.setFocus(prevInput);
      $event.preventDefault();
    }
  }

  mouseDownResizeHandler($event) {
    this.resizeX =
      $event.type === 'touchstart' ? $event.touches[0].clientX : $event.clientX;
    const styles = globalThis.getComputedStyle(
      $event.target.parentElement.parentElement,
    );
    this.resizeColumnWidth = parseInt(styles.width, 10);
    this.resizeColumn = $event.target.parentElement.parentElement;

    document.addEventListener('mousemove', this.mouseMoveResizeHandler);
    document.addEventListener('touchmove', this.mouseMoveResizeHandler);
    document.addEventListener('mouseup', this.mouseUpResizeHandler);
    document.addEventListener('touchend', this.mouseUpResizeHandler);
  }

  private mouseMoveResizeHandler = ($event) => {
    if (this.resizeColumn) {
      $event.preventDefault();
      const dx =
        $event.type === 'touchmove'
          ? $event.touches[0].clientX - this.resizeX
          : $event.clientX - this.resizeX;
      const newWidth = this.resizeColumnWidth + dx;

      if (dx > 0) {
        const headerRow = this.resizeColumn.parentElement;
        const headerCols = headerRow.querySelectorAll('th');
        let allHasSetWidth = true;
        let smallestCol;
        let smallestColWidth = Number.MAX_VALUE;
        for (const headerCol of headerCols) {
          if (headerCol.clientWidth < 100) {
            return;
          }

          if (
            headerCol.clientWidth < smallestColWidth &&
            headerCol !== this.resizeColumn
          ) {
            smallestCol = headerCol;
            smallestColWidth = headerCol.clientWidth;
          }

          if (headerCol.style.width === '') {
            allHasSetWidth = false;
          }
        }

        if (allHasSetWidth) {
          smallestCol.style.width = '';
        }
      } else if (newWidth < 100) {
        return;
      }

      this.resizeColumn.style.width = `${newWidth}px`;
    }
  };

  private mouseUpResizeHandler = () => {
    document.removeEventListener('mousemove', this.mouseMoveResizeHandler);
    document.removeEventListener('touchmove', this.mouseMoveResizeHandler);
    document.removeEventListener('mouseup', this.mouseUpResizeHandler);
    document.removeEventListener('touchend', this.mouseUpResizeHandler);
  };

  private focusInTable() {
    return (
      document.activeElement.classList.contains('focus-visible') &&
      ((this.tableRef().nativeElement as HTMLElement).contains(
        document.activeElement,
      ) ||
        this.tableRef().nativeElement === document.activeElement)
    );
  }

  private goToNextCell() {
    let nextInput =
      document.activeElement.parentElement.parentElement.nextElementSibling?.querySelector(
        'input',
      );
    if (!nextInput) {
      const rowElement = document.activeElement.parentElement.parentElement;
      nextInput =
        rowElement.parentElement.nextElementSibling?.querySelector('input') ??
        (document.activeElement as HTMLInputElement);
    }
    this.setFocus(nextInput);
  }

  private setFocus(input: HTMLElement) {
    input.focus();
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        input.classList.add('focus-visible', 'focus');
        input.parentElement.parentElement.style.zIndex = '1';
      });
    });
  }

  private setValues(value?: string) {
    if (
      this.readonlyData() ||
      this.notExpandedAndShowExpand ||
      this.readonly() ||
      this.readOnlyState
    ) {
      this.setReadonlyValues(value ? JSON.parse(value) : []);

      if (this.notExpandedAndShowExpand || this.readOnlyState) {
        this.cdr.markForCheck();
        return;
      }
    }

    if (this.subscribes) {
      for (const subscribe of this.subscribes) {
        subscribe.unsubscribe();
      }
    }
    this.subscribes = [];

    this.values = [];
    const parsedValues = value ? JSON.parse(value) : [];
    for (const [rowIndex, row] of this.rows().entries()) {
      const valueRow = [];
      for (const [columnIndex, col] of this.columns().entries()) {
        if (row.readonly || col.readonly) {
          continue;
        }

        const parsedValue = parsedValues.find(
          (pv) => pv.rowId === row.id && pv.columnId === col.id,
        );
        const cellValue = parsedValue?.value ?? this.defaultValue();

        const control = this.fb.control(
          cellValue,
          this.required() ? [Validators.required, ValidateSpace] : undefined,
        );

        this.subscribes.push(
          control.valueChanges
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe(() => this.inputChange()),
          merge(
            control.valueChanges,
            control.status$.pipe(skip(1), distinctUntilChanged()),
            control.touch$.pipe(take(1)),
            control.dirty$.pipe(take(1)),
          )
            .pipe(debounceTime(0), untilDestroyed(this))
            .subscribe(() => this.checkInvalid(control, rowIndex, columnIndex)),
        );

        valueRow.push({
          rowId: row.id,
          columnId: col.id,
          control: control,
          invalid: false,
        });

        if (this.inputType() === InputType.Amount && cellValue > 99_999_999) {
          this.smallNumbers = true;
        }
      }
      this.values.push(valueRow);
    }
    if (this.ngControl?.touched || this.markAllAsTouched()) {
      for (const value of this.values.flat()) value.control.markAsTouched();
    }

    this.setSums();
    this.cdr.markForCheck();
  }

  private setReadonlyValues(dataFromControl?) {
    const oldReadonlyValues = this.readonlyValues;
    this.readonlyValues = [];
    for (const [rowIndex, row] of this.rows().entries()) {
      const valueRow = [];
      for (const [colIndex, col] of this.columns().entries()) {
        let readonlyValue;
        const readonlyValueIndex =
          this.readonlyData()?.findIndex(
            (d) => d.rowId === row.id && d.columnId === col.id,
          ) ?? -1;
        if (readonlyValueIndex > -1) {
          readonlyValue = this.readonlyData()[readonlyValueIndex].value;
        } else if (dataFromControl) {
          const value =
            dataFromControl.find(
              (pv) => pv.rowId === row.id && pv.columnId === col.id,
            )?.value ?? 0;
          readonlyValue = value;
        } else if (oldReadonlyValues && oldReadonlyValues.length > 0) {
          readonlyValue = oldReadonlyValues[rowIndex][colIndex];
        }

        if (
          this.inputType() === InputType.Amount &&
          readonlyValue > 99_999_999
        ) {
          this.smallNumbers = true;
        }

        valueRow.push(readonlyValue);
      }
      this.readonlyValues.push(valueRow);
    }
    this.setSums();
  }

  private inputChange() {
    if (this.inputType() === InputType.Amount) {
      let hasLargeValue = false;
      for (const value of this.values.flat()) {
        if ((value.control.value as number) > 99_999_999) {
          hasLargeValue = true;
        }
      }
      this.smallNumbers = hasLargeValue;
    }

    const values = this.values
      .flat()
      .filter((v) => v.control.value || v.control.value === 0)
      .map((v) => {
        return {
          rowId: v.rowId,
          columnId: v.columnId,
          value: v.control.value,
        };
      });
    this.onChange(values.length > 0 ? JSON.stringify(values) : undefined);
    this.setSums();
    this.errors = [];
    this.warnings = [];
    if (
      this.ngControl?.valid ||
      (this.tableValidation() &&
        !this.tableValidation().some((v) => this.ngControl?.errors[v]))
    ) {
      this.tableInvalid = false;
    } else if (
      (this.ngControl?.touched || this.readOnlyState) &&
      this.ngControl?.invalid &&
      this.tableValidation() &&
      this.tableValidation().some((v) => this.ngControl?.errors[v])
    ) {
      this.tableInvalid = true;
      this.errors.push(
        ...this.tableValidation().filter((v) => this.ngControl?.errors[v]),
      );
    }
    this.setRowValidation();
    this.cdr.markForCheck();
  }

  @HostListener('window:resize')
  setShowCols() {
    // When printing, show all columns
    if (this.isPrinting) {
      this.maxCols = this.columns().length;
      this.startCol = 0;
      this.showCol = Array.from(
        { length: this.columns().length },
        (_x, i) => i,
      );
      this.showRowSum.set(this.sumRow());
      return;
    }

    if (window.innerWidth >= 992 && this.browseLarge()) {
      this.setBrowse(this.browseLarge());
    } else if (window.innerWidth >= 576 && this.browseMedium()) {
      this.setBrowse(this.browseMedium());
    } else if (window.innerWidth < 576 && this.browseSmall()) {
      this.setBrowse(this.browseSmall());
    } else {
      this.maxCols =
        window.innerWidth >= 992 ? this.maxColsDesktop() : this.maxColsMobile();
      this.showCol = Array.from(
        { length: Math.min(this.maxCols, this.columns().length) },
        (_x, i) => i,
      );
    }
  }

  private setBrowse(browse: { start: number; length: number }[]) {
    this.showRowSum.set(false);
    if (this.browseIndex >= browse.length) {
      this.showRowSum.set(true);
      this.browseIndex = browse.length - 1;
    }
    if (this.browseIndex >= browse.length - 1) {
      this.showRowSum.set(true);
    }
    const b = browse[this.browseIndex];
    this.maxCols = b.length;
    this.startCol = b.start;
    this.showCol = Array.from({ length: b.length }, (_x, i) => i + b.start);
  }

  private setSums() {
    const values = this.values?.map((a) => a.map((b) => b.control.value));
    this.rowSums = Array.from({ length: this.rows().length }, () => 0);
    this.colSums = Array.from({ length: this.columns().length }, () => 0);

    this.totalSum = 0;
    for (const [rowIndex, row] of this.rows().entries()) {
      for (const [colIndex, col] of this.columns().entries()) {
        let value;
        if (
          this.notExpandedAndShowExpand ||
          row.readonly ||
          col.readonly ||
          this.readonly()
        ) {
          value = this.readonlyValues
            ? +this.readonlyValues[rowIndex][colIndex]
            : 0;
        } else {
          value =
            values && values[rowIndex] && values[rowIndex][colIndex]
              ? +values[rowIndex][colIndex]
              : 0;
        }
        this.rowSums[rowIndex] += value;
        this.colSums[colIndex] += value;
        this.totalSum += value;
      }
    }

    const overriddenSums = this.readonlyData()?.filter(
      (readonly) => readonly.rowId === 'sum',
    );
    if (!overriddenSums) {
      return;
    }
    for (const overriddenSum of overriddenSums) {
      const colIndex = this.columns().findIndex(
        (column) => column.id === overriddenSum.columnId,
      );
      this.colSums[colIndex] = parseFloat(overriddenSum.value.toString());
    }
  }

  private setValidation() {
    if (this.hasAnyValue()) {
      this.ngControl.control.markAsTouched();
    }
    this.errors = [];
    this.warnings = [];
    if (
      this.ngControl?.invalid &&
      (this.ngControl?.touched || this.readOnlyState) &&
      this.tableValidation() &&
      this.tableValidation().some((v) => this.ngControl?.errors[v])
    ) {
      this.tableInvalid = true;
      this.errors.push(
        ...this.tableValidation().filter((v) => this.ngControl?.errors[v]),
      );
    } else {
      this.tableInvalid = false;
    }

    this.setWarnings();
    this.setRowValidation();
    this.cdr.markForCheck();
  }

  setWarnings() {
    if (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.ngControl?.control as any).warnings &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.ngControl?.control as any).warnings['BudgetNoCostsReportedWarning']
    ) {
      this.warnings.push('BudgetNoCostsReportedWarning');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const warnings = (this.ngControl?.control as any).warnings;
    if (
      warnings &&
      Object.keys(warnings).some((key) =>
        key.startsWith('BudgetNoBudgetForKostnadslag'),
      )
    ) {
      this.warnings.push('BudgetNoBudgetForKostnadslag');
    }
  }

  private setRowValidation() {
    this.rowInvalid = [];
    this.rowWarning = [];
    if (
      (this.ngControl?.touched || this.readOnlyState) &&
      this.tableRowValidation()
    ) {
      for (const rowValidation of this.tableRowValidation()) {
        const rowKey = `${rowValidation.validation}_${rowValidation.rowId}`;
        if (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (this.ngControl?.control as any).warnings &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (this.ngControl?.control as any).warnings[rowKey]
        ) {
          this.rowWarning.push(rowValidation.rowId);
          this.warnings.push(rowValidation.validation);
        } else if (this.ngControl?.errors && this.ngControl?.errors[rowKey]) {
          this.rowInvalid.push(rowValidation.rowId);
          this.errors.push(rowValidation.validation);
        }
      }
    }
  }

  private hasAnyValue() {
    if (!this.ngControl?.control?.value) {
      return false;
    }

    const parsed = JSON.parse(this.ngControl.control.value);
    return parsed.some((c) => c.value > 0);
  }

  tableClick() {
    if (this.notExpandedAndShowExpand && !this.expanded()) {
      this.animatedBtnRef().nativeElement.classList.add('animate');
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          this.animatedBtnRef().nativeElement.classList.remove('animate');
        }, 1250);
      });
    }
  }

  getChangeTextTooltip(changes: ITableChange[]) {
    return changes
      .map(
        (c) =>
          `<div class="change-row">${this.getChangeText(c)}: ${new Intl.NumberFormat('sv-SE').format(c.value)} ${format(c.date, 'yyyy-MM-dd')}</div>`,
      )
      .join('');
  }

  getChangeTextDescription(changes: ITableChange[]) {
    return changes
      .map(
        (c) =>
          `${this.translocoService.getActiveLang() === 'sv' ? c.text : c.textEn} ${new Intl.NumberFormat('sv-SE').format(c.value)} ${format(c.date, 'yyyy-MM-dd')}`,
      )
      .join(' ');
  }

  toggle() {
    this.toggleState = !this.toggleState;
    this.toggleOutput.emit(this.toggleState);
  }

  private getChangeText(change: ITableChange): string {
    if (change.text && change.textEn) {
      return this.translocoService.getActiveLang() === 'sv'
        ? change.text
        : change.textEn;
    }
    if (change.tooltipTextKey) {
      return this.translocoService.translate(change.tooltipTextKey);
    }

    return undefined;
  }
}
